



import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Offcanvas, Button } from 'react-bootstrap';
import {
    FaBuilding,
    FaTag,
    FaCalendar,
    FaDollarSign,
    FaFileInvoice,
    FaUsers,
    FaExchangeAlt,
    FaHistory
} from 'react-icons/fa';
import { BiPencil } from "react-icons/bi";
import { BsX } from "react-icons/bs";
import ProjectView from './ProjectView';
import ProjectList from './ProjectList';
import CommunicationDetails from './CommunicationDetails';
import QuotationList from '../quotation/QuotationList';
import SelectedContact from '../contact/SelectedContact';
import ProformaInvoiceList from '../ProformaInvoice/ProformaInvoiceList';
import withPermission from '../../utils/withPermission';
import ExpenseList from './expense/ExpenseList';
import ProjectModal from './ProjectModal';

const ProjectDetails = ({ show, onHide, projectId, parentId }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get("activeTab") || "subprojectList";

    const [projectDetails, setProjectDetails] = useState(null);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [quotationHistory, setQuotationHistory] = useState([]);
    const [isSales, setIsSales] = useState(false);
    const [isSubproject, setIsSubproject] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [clients, setClients] = useState([]);
    const [isTemporarilyHidden, setIsTemporarilyHidden] = useState(false);
    const [localShow, setLocalShow] = useState(show);

    const WithSubPermission = withPermission(({ children }) => children, ['project-list']);
    const WithCommuniPermission = withPermission(({ children }) => children, ['communication-list']);
    const WithQuoPermission = withPermission(({ children }) => children, ['quotation-list']);
    const WithContactsPermission = withPermission(({ children }) => children, ['contact-list']);
    const WithExpensePermission = withPermission(({ children }) => children, ['expense-list']);

    // Sync with parent show prop
    useEffect(() => {
        setLocalShow(show);
    }, [show]);

    // Fetch project details
    const fetchProjectDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            const response = await axios.get(`${backendUrl}/api/projects/${currentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            const data = response.data.Data;
            setProjectDetails(data);
            setIsSubproject(!!data.parent_id);
            setIsSales(!!data.issales);
        } catch (error) {
            console.error('Error fetching project details:', error);
        }
    };

    // Fetch quotation history
    const fetchQuotationHistory = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const currentId = projectId || id;
            const response = await axios.get(`${backendUrl}/api/quotations/${currentId}/quotation-history`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setQuotationHistory(response.data.data || []);
        } catch (error) {
            console.error('Error fetching quotation history:', error);
        }
    };

    // Fetch clients
    const fetchClients = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/contacts`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setClients(response.data.Data || []);
        } catch (error) {
            console.error('Error fetching clients:', error);
        }
    };

    // Edit project function
    const editProject = async (projectId, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(
                `${backendUrl}/api/projects/${projectId}?_method=PUT`,
                updatedData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                        'Tenant-Origin': origin,
                    },
                }
            );

            const updatedProject = response.data.Data;

            if (updatedProject) {
                setProjectDetails((prevProject) => ({
                    ...prevProject,
                    ...updatedProject,
                }));
            }

            setShowModal(false);
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors;
            } else {
                console.error('Error updating project:', error);
            }
        }
    };

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    // Format action helper
    const formatAction = (action) => {
        if (!action) return '';
        return action
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    useEffect(() => {
        if (localShow && (projectId || id)) {
            setProjectDetails(null);
            fetchProjectDetails();
            fetchClients();
        }
    }, [localShow, projectId, id]);

    useEffect(() => {
        if (localShow && activeTab === 'quotationHistory') {
            fetchQuotationHistory();
        }
    }, [localShow, activeTab]);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const handleOffcanvasClose = () => {
        setProjectDetails(null);
        setActiveTab('subprojectList');
        onHide();
    };

    // Callback functions for temporarily hiding the offcanvas
    const temporarilyHideOffcanvas = () => {
        setIsTemporarilyHidden(true);
    };

    const reopenOffcanvas = () => {
        setIsTemporarilyHidden(false);
    };

    const shouldShowOffcanvas = show && !isTemporarilyHidden;

    return (
        <Offcanvas
            show={localShow}
            onHide={handleOffcanvasClose}
            placement="end"
            style={{ width: '1050px', zIndex: 1045 }}
            className="Project-offcanvas"
        >
            {/* Offcanvas Header */}
            <Offcanvas.Header className="border-bottom bg-white py-3">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center">
                        <div className="bg-primary rounded-circle p-2 me-3">
                            <FaBuilding className="text-white" size={18} />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">{projectDetails?.name}</h5>
                            <small className="text-muted">ID: {projectDetails?.code || projectId || id}</small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                                setSelectedProject(projectDetails);
                                setShowModal(true);
                            }}
                        >
                            <BiPencil className="me-1" />
                            Edit
                        </Button>
                        <Button
                            variant="light"
                            size="sm"
                            onClick={handleOffcanvasClose}
                            className="border-0"
                        >
                            <BsX size={20} />
                        </Button>
                    </div>
                </div>
            </Offcanvas.Header>

            {/* Offcanvas Body */}
            <Offcanvas.Body className="p-0">
                {/* Project Summary Card */}
                <div className="p-4 border-bottom bg-light">
                    <div className="row g-4">
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaTag className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Project Code</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {projectDetails?.code || 'Not assigned'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaCalendar className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Start Date</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {formatDate(projectDetails?.start_date) || 'Not specified'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaDollarSign className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Expenditure Amount</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {projectDetails?.expenditure_amount || '0.00'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-center">
                                <div className="bg-white rounded-circle p-2 shadow-sm me-3">
                                    <FaUsers className="text-primary" size={16} />
                                </div>
                                <div>
                                    <label className="text-muted small mb-1 d-block">Client</label>
                                    <h6 className="mb-0 fw-semibold text-dark">
                                        {projectDetails?.client?.name || 'Not assigned'}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="border-bottom bg-white">
                    <ul className="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
                        {!isSales && !isSubproject && (
                            <WithSubPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'subprojectList' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('subprojectList')}
                                        role="tab"
                                    >
                                        <FaBuilding className="me-2" size={14} />
                                        Sub Projects
                                    </button>
                                </li>
                            </WithSubPermission>
                        )}

                        <WithQuoPermission>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'quotationList' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('quotationList')}
                                    role="tab"
                                >
                                    <FaFileInvoice className="me-2" size={14} />
                                    Quotations
                                </button>
                            </li>
                        </WithQuoPermission>

                        {/* <WithQuoPermission>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'quotationHistory' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('quotationHistory')}
                                    role="tab"
                                >
                                    <FaHistory className="me-2" size={14} />
                                    Quotation History
                                </button>
                            </li>
                        </WithQuoPermission> */}

                        <WithExpensePermission>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'expense' ? 'active' : ''}`}
                                    onClick={() => handleTabClick('expense')}
                                    role="tab"
                                >
                                    Expenses
                                </button>
                            </li>
                        </WithExpensePermission>

                        {!isSubproject && (
                            <WithCommuniPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'communication' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('communication')}
                                        role="tab"
                                    >
                                        Communications
                                    </button>
                                </li>
                            </WithCommuniPermission>
                        )}

                        {!isSales && (
                            <WithContactsPermission>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'contactList' ? 'active' : ''}`}
                                        onClick={() => handleTabClick('contactList')}
                                        role="tab"
                                    >
                                        <FaUsers className="me-2" size={14} />
                                        Contacts
                                    </button>
                                </li>
                            </WithContactsPermission>
                        )}

                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'projectDetails' ? 'active' : ''}`}
                                onClick={() => handleTabClick('projectDetails')}
                                role="tab"
                            >
                                Project Details
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Tab Content */}
                <div className="tab-content p-4 bg-white" style={{ minHeight: '500px', maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                    {/* Sub Projects Tab */}
                    {activeTab === "subprojectList" && projectDetails && (
                        <div className="tab-pane active">
                            <ProjectList
                                clientId={projectDetails?.client_id}
                                parentId={projectId || id}
                                onCloseOffcanvas={temporarilyHideOffcanvas}
                                onReopenOffcanvas={reopenOffcanvas}
                            />
                        </div>
                    )}

                    {/* Quotations Tab */}
                    {activeTab === "quotationList" && projectDetails && (
                        <div className="tab-pane active">
                            <QuotationList
                                projectId={projectId || id}
                                clientId={projectDetails?.client_id || projectDetails?.client?.id}
                                projectName={projectDetails?.name}
                                clientName={projectDetails?.client?.name}
                                clientCurrency={projectDetails?.client?.currency}
                                projectData={projectDetails}
                                setProjectDetails={setProjectDetails}
                            />
                        </div>
                    )}

                    {/* Quotation History Tab */}
                    {activeTab === "quotationHistory" && projectDetails && (
                        <div className="tab-pane active">
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th scope="col">#</th>
                                            <th scope="col">Date</th>
                                            <th scope="col">Quotation ID</th>
                                            <th scope="col">Action</th>
                                            <th scope="col">Description</th>
                                            <th scope="col">Document</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quotationHistory.map((quotation, index) => (
                                            quotation.history && quotation.history.map((historyItem, idx) => (
                                                <tr key={`${index}-${idx}`}>
                                                    <td>{idx + 1}</td>
                                                    <td>{formatDate(quotation.date)}</td>
                                                    <td>{quotation.ref_no}</td>
                                                    <td>{formatAction(historyItem.action)}</td>
                                                    <td>{historyItem.description}</td>
                                                    <td>
                                                        {historyItem.attachment ? (
                                                            <a
                                                                href={`${process.env.REACT_APP_BACKEND_URL}/storage/${historyItem.attachment}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-outline-primary"
                                                            >
                                                                View
                                                            </a>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            ))
                                        ))}
                                        {quotationHistory.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4 text-muted">
                                                    No quotation history available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Expenses Tab */}
                    {activeTab === "expense" && projectDetails && (
                        <div className="tab-pane active">
                            <ExpenseList
                                projectId={projectId || id}
                                projectDetails={projectDetails}
                            />
                        </div>
                    )}

                    {/* Communications Tab */}
                    {activeTab === "communication" && projectDetails && (
                        <div className="tab-pane active">
                            <CommunicationDetails
                                projectId={projectId || id}
                            />
                        </div>
                    )}

                    {/* Contacts Tab */}
                    {activeTab === "contactList" && projectDetails && (
                        <div className="tab-pane active">
                            <SelectedContact
                                projectId={projectId || id}
                                entityType="project"
                                fetchProjectData={fetchProjectDetails}
                                projectData={projectDetails}
                                setProjectDetails={setProjectDetails}
                            />
                        </div>
                    )}

                    {/* Project Details Tab */}
                    {activeTab === "projectDetails" && (
                        <div className="tab-pane active">
                            {projectDetails ? (
                                <ProjectView
                                    data={projectDetails}
                                    setProjectDetails={setProjectDetails}
                                />
                            ) : (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading project details...</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Offcanvas.Body>

            {/* Project Modal */}
            {showModal && (
                <ProjectModal
                    showModal={showModal}
                    handleClose={() => setShowModal(false)}
                    projectId={projectId || id}
                    projectData={selectedProject}
                    editProject={editProject}
                    clients={clients}
                    fetchClients={fetchClients}
                    defaultClientId={selectedProject?.client_id}
                />
            )}
        </Offcanvas>
    );
};

export default ProjectDetails;
